// components/UserTokens.jsx
import React from 'react';
import {
    Card,
    CardContent,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Avatar,
    Skeleton,
    Box,
    Pagination,
    useTheme,
} from '@mui/material';
import { transformToBTC } from '../../utils/helpers';
import { useUserTokens } from '../../hooks';
import { useNavigate } from 'react-router-dom';
import { useBitcoinPrice } from '../../state/context/bitcoin.context';

const UserTokens = ({ userId }) => {
    const { data, loading, error, page, setPage, hasNextPage } =
        useUserTokens({ userId, limit: 10 });
    const navigate = useNavigate();
    const theme = useTheme();
    const { price: btcPrice } = useBitcoinPrice();

    if (loading) {
        return (
            <Skeleton
                variant="rectangular"
                sx={{
                    borderRadius: 2,
                    width: '100%',
                    height: 300,
                }}
            />
        );
    }

    if (error) {
        return (
            <Typography color="error" sx={{ m: 2 }}>
                Error: {error.message}
            </Typography>
        );
    }

    return (
        <Card
            sx={{
                borderRadius: 2,
                boxShadow: 3,
                m: 2,
            }}
        >
            <CardContent>
                <Typography
                    variant="h5"
                    sx={{ fontWeight: 'bold', mb: 2 }}
                >
                    Trades by Token
                </Typography>

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Token</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Total Volume</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Trade Count</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Buy Count</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Sell Count</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Buy Volume</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Sell Volume</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Profit/Loss</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {data?.trades?.map((token, idx) => (
                            <TableRow
                                key={idx}
                                onClick={() => window.open(`/tokens/${ token._id }`)}
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                        bgcolor: theme.palette.mode === 'light'
                                            ? 'rgba(0,0,0,0.04)'
                                            : 'rgba(255,255,255,0.1)'
                                    },
                                }}
                            >
                                <TableCell
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <Avatar
                                        src={`https://images.odin.fun/token/${ token._id }`}
                                        alt={token.ticker}
                                        sx={{ width: 24, height: 24 }}
                                    />
                                    {token.ticker}
                                </TableCell>
                                <TableCell>
                                    <Box>
                                        <Typography variant="body2">
                                            {transformToBTC(token.totalBTCVolume)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            ${((token.totalBTCVolume / 1e11) * btcPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    {token.tradeCount}
                                </TableCell>
                                <TableCell>
                                    {token.buyCount}
                                </TableCell>
                                <TableCell>
                                    {token.sellCount}
                                </TableCell>
                                <TableCell>
                                    <Box>
                                        <Typography variant="body2">
                                            {transformToBTC(token.totalBuyBTCVolume)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            ${((token.totalBuyBTCVolume / 1e11) * btcPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Box>
                                        <Typography variant="body2">
                                            {transformToBTC(token.totalSellBTCVolume)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            ${((token.totalSellBTCVolume / 1e11) * btcPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    {(() => {
                                        const profit = token.totalSellBTCVolume - token.totalBuyBTCVolume;
                                        const profitUSD = (profit / 1e11) * btcPrice;
                                        const isProfit = profit > 0;
                                        return (
                                            <Box>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: isProfit ? theme.palette.success.main : profit < 0 ? theme.palette.error.main : 'inherit',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {isProfit ? '+' : ''}{transformToBTC(profit)}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: isProfit ? theme.palette.success.main : profit < 0 ? theme.palette.error.main : 'text.secondary'
                                                    }}
                                                >
                                                    {isProfit ? '+' : ''}${profitUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </Box>
                                        );
                                    })()}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                        count={hasNextPage ? page + 1 : page}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        color="primary"
                        size="small"
                    />
                </Box>
            </CardContent>
        </Card>
    );
};

export default UserTokens;
